// Unified Router - Combines MOPUI and MoceroUI routes into single application
import type { RouteRecordRaw } from 'vue-router';

// MOPUI Components (existing)
import LoginDashboard from '../views/dashboard/DashboardIndex.vue';
import NewsEventsPage from '../views/dashboard/NewsEventsPage.vue';
import CircularsPage from '../views/dashboard/CircularsPage.vue';
import SOPDocumentsPage from '../views/dashboard/SOPDocumentsPage.vue';
import NewsEventDetailPage from '../views/dashboard/NewsEventDetailPage.vue';
import NewsEventFormPage from '../views/dashboard/NewsEventFormPage.vue';
import CircularDetailPage from '../views/dashboard/CircularDetailPage.vue';
import CircularFormPage from '../views/dashboard/CircularFormPage.vue';
import SOPDocumentDetailPage from '../views/dashboard/SOPDocumentDetailPage.vue';
import SOPDocumentFormPage from '../views/dashboard/SOPDocumentFormPage.vue';
import AuthLogin from '../views/auth/LoginView.vue';
import AuthForgotPassword from '../views/auth/ForgotPasswordView.vue';

// Import MOPUI accounts routes
import { accountsRoutes } from '../modules/accounts/router/index';

// Import MoceroUI Components lazily to reduce build memory usage
// CRM Components
const CRMLayout = () => import('../../../MoceroUI/src/modules/crm/components/Layout/Header.vue');
const CRMDashboard = () => import('../../../MoceroUI/src/modules/crm/pages/dashboard/dashboard.vue');
const CRMLogin = () => import('../../../MoceroUI/src/modules/crm/pages/Login/login.vue');

// Inventory Components
const InventoryLayout = () => import('../../../MoceroUI/src/modules/Inventory/components/Layout/Header.vue');
const InventoryMasters = () => import('../../../MoceroUI/src/modules/Inventory/pages/MastersforInventory/MastersforInventory.vue');

// User Management Components
const UserManagementLayout = () => import('../../../MoceroUI/src/modules/userManagement/components/Layout/Header.vue');
const UserManagementDashboard = () => import('../../../MoceroUI/src/modules/userManagement/pages/dashboard.vue');
import { TaskRoutes } from '../modules/TaskManagement/router/router'

import {hrRoutes} from '../modules/hr/router/index'

export const unifiedRoutes: RouteRecordRaw[] = [
    {
        path: '/',
        redirect: '/auth/login'
    },

    // Unified Authentication Routes
    {
        path: '/auth',
        children: [
            {
                path: 'login',
                name: 'Login',
                component: AuthLogin // Use MOPUI login as primary
            },
            {
                path: 'forgot-password',
                name: 'ForgotPassword',
                component: AuthForgotPassword
            }
        ]
    },

    // Unified Dashboard
    {
        path: '/dashboard',
        name: 'Dashboard',
        component: LoginDashboard,
        meta: { requiresAuth: true }
    },

    // Dashboard detail pages
    {
        path: '/news-events',
        name: 'NewsEvents',
        component: NewsEventsPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/circulars',
        name: 'Circulars',
        component: CircularsPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/sop-documents',
        name: 'SOPDocuments',
        component: SOPDocumentsPage,
        meta: { requiresAuth: true }
    },

    // News & Events detail + form routes
    {
        path: '/news-events/create',
        name: 'NewsEventCreate',
        component: NewsEventFormPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/news-events/:id',
        name: 'NewsEventDetail',
        component: NewsEventDetailPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/news-events/:id/edit',
        name: 'NewsEventEdit',
        component: NewsEventFormPage,
        meta: { requiresAuth: true }
    },

    // Circulars detail + form routes
    {
        path: '/circulars/create',
        name: 'CircularCreate',
        component: CircularFormPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/circulars/:id',
        name: 'CircularDetail',
        component: CircularDetailPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/circulars/:id/edit',
        name: 'CircularEdit',
        component: CircularFormPage,
        meta: { requiresAuth: true }
    },

    // SOP Documents detail + form routes
    {
        path: '/sop-documents/create',
        name: 'SOPDocumentCreate',
        component: SOPDocumentFormPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/sop-documents/:id',
        name: 'SOPDocumentDetail',
        component: SOPDocumentDetailPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/sop-documents/:id/edit',
        name: 'SOPDocumentEdit',
        component: SOPDocumentFormPage,
        meta: { requiresAuth: true }
    },

    // Direct redirect for /accounts to MOPLayout
    // {
    //     path: '/accounts',
    //     redirect: '/MOPLayout/accounts'
    // },

    // MOPUI Routes (Modern Modules) - All routes go through MOPLayout
    {
        path: '/MOPLayout',
        name: 'MOPLayout',
        component: () => import('../Layout/MOPLayout.vue'),
        meta: { requiresAuth: true, moduleType: 'mopui' },
        children: [
            ...accountsRoutes,
            ...TaskRoutes,
            ...hrRoutes
        ]
    },

    // MoceroUI Routes (Legacy Modules) - Integrated directly
    {
        path: '/crm',
        name: 'CRMModule',
        component: CRMLayout,
        meta: { requiresAuth: true, moduleType: 'moceroui' },
        children: [
            {
                path: '',
                name: 'CRMModuleDefault',
                redirect: '/crm/dashboard'
            },
            {
                path: 'dashboard',
                name: 'CRMDashboard',
                component: CRMDashboard,
                meta: { requiresAuth: true }
            }
        ]
    },

    // MoceroUI Inventory routes are registered dynamically via registerInventoryRoutes()
    // to avoid eagerly importing 100+ components and causing OOM during build

    {
        path: '/user-management',
        name: 'UserManagementModule',
        component: UserManagementLayout,
        meta: { requiresAuth: true, moduleType: 'moceroui' },
        children: [
            {
                path: '',
                name: 'UserManagementModuleDefault',
                redirect: '/user-management/dashboard'
            },
            {
                path: 'dashboard',
                name: 'UserManagementDashboard',
                component: UserManagementDashboard,
                meta: { requiresAuth: true }
            }
        ]
    },

    // Legacy route redirects for backward compatibility
    {
        path: '/Layout/dashboard',
        redirect: '/crm/dashboard'
    },
    {
        path: '/InventoryLayout',
        redirect: '/inventory'
    },
    {
        path: '/InventoryLayout/Masters',
        redirect: '/inventory/masters'
    },
    {
        path: '/billingLayout/userdashboard',
        redirect: '/user-management/dashboard'
    }
];
